// PATH: src/theme/theme.ts
/**
 * Thème unique de l'admin : toutes les pages héritent d'ici (couleurs, rayons,
 * densité des tables, boutons). Une page ne pose pas de couleur en dur : elle
 * lit `palette` ou les jetons `tokens` ci-dessous.
 */
import { alpha, createTheme } from "@mui/material/styles";
import { frFR } from "@mui/material/locale";

/** Jetons partagés hors palette MUI (états de couverture, bordures). */
export const tokens = {
  border: "#E4E7EC",
  borderStrong: "#D0D5DD",
  surfaceMuted: "#F8F9FB",
  sidebar: "#101828",
  sidebarText: "#D0D5DD",
  sidebarMuted: "#98A2B3",
  /**
   * Couverture : à produire → importé → jouable ; `missing` = offre du
   * catalogue sans ossature (non couverte, et rien ne peut s'y rédiger).
   */
  coverage: {
    none: "#EAECF0",
    noneText: "#667085",
    imported: "#F79009",
    importedSoft: "#FEF0C7",
    playable: "#12B76A",
    playableSoft: "#D1FADF",
    missing: "#F04438",
    missingSoft: "#FEE4E2",
    missingText: "#B42318",
  },
} as const;

const primary = "#444CE7";

export const theme = createTheme(
  {
    palette: {
      mode: "light",
      primary: { main: primary, light: "#8098F9", dark: "#2D31A6", contrastText: "#fff" },
      secondary: { main: "#475467" },
      success: { main: "#12B76A", dark: "#027A48", light: "#D1FADF" },
      warning: { main: "#F79009", dark: "#B54708", light: "#FEF0C7" },
      error: { main: "#F04438", dark: "#B42318", light: "#FEE4E2" },
      info: { main: "#2E90FA", dark: "#175CD3", light: "#D1E9FF" },
      text: { primary: "#101828", secondary: "#475467", disabled: "#98A2B3" },
      divider: tokens.border,
      background: { default: "#F5F6F8", paper: "#FFFFFF" },
      action: { hover: alpha("#101828", 0.04), selected: alpha(primary, 0.08) },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter", "Noto Sans Arabic", system-ui, -apple-system, "Segoe UI", sans-serif',
      fontSize: 14,
      h1: { fontSize: "1.75rem", fontWeight: 650, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.5rem", fontWeight: 650, letterSpacing: "-0.02em" },
      h3: { fontSize: "1.25rem", fontWeight: 650, letterSpacing: "-0.01em" },
      h4: { fontSize: "1.375rem", fontWeight: 650, letterSpacing: "-0.015em" },
      h5: { fontSize: "1.125rem", fontWeight: 600 },
      h6: { fontSize: "1rem", fontWeight: 600 },
      subtitle1: { fontSize: "0.9375rem", fontWeight: 600 },
      subtitle2: { fontSize: "0.8125rem", fontWeight: 600 },
      body1: { fontSize: "0.875rem" },
      body2: { fontSize: "0.8125rem" },
      caption: { fontSize: "0.75rem" },
      overline: { fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.06em" },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: "antialiased" },
          "code, pre, .mono": {
            fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: "0.8em",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 14, minHeight: 36 },
          sizeSmall: { minHeight: 30, paddingInline: 10 },
          outlined: ({ theme: t }) => ({
            borderColor: tokens.borderStrong,
            color: t.palette.text.primary,
            backgroundColor: "#fff",
            "&:hover": { borderColor: tokens.borderStrong, backgroundColor: tokens.surfaceMuted },
          }),
          outlinedError: ({ theme: t }) => ({
            color: t.palette.error.dark,
            borderColor: alpha(t.palette.error.main, 0.4),
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: tokens.border },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 14 } },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: "1.0625rem", fontWeight: 650, padding: "18px 24px 10px" } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: "12px 24px 18px", gap: 8 } },
      },
      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: tokens.border, paddingTop: 10, paddingBottom: 10 },
          head: ({ theme: t }) => ({
            fontSize: "0.75rem",
            fontWeight: 600,
            color: t.palette.text.secondary,
            backgroundColor: tokens.surfaceMuted,
            whiteSpace: "nowrap",
          }),
          sizeSmall: { paddingTop: 7, paddingBottom: 7 },
        },
      },
      MuiTableRow: {
        styleOverrides: { root: { "&:last-child td": { borderBottom: 0 } } },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 6 },
          sizeSmall: { height: 22, fontSize: "0.75rem" },
          outlined: { borderColor: tokens.borderStrong },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 300 },
        styleOverrides: { tooltip: { fontSize: "0.75rem", backgroundColor: "#101828" }, arrow: { color: "#101828" } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
          },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiSelect: { defaultProps: { size: "small" } },
      MuiFormControl: { defaultProps: { size: "small" } },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, alignItems: "center" } },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 40 }, indicator: { height: 2, borderRadius: 2 } },
      },
      MuiTab: {
        styleOverrides: { root: { minHeight: 40, textTransform: "none", fontWeight: 600, paddingInline: 12 } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 99, height: 6 }, bar: { borderRadius: 99 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiSwitch: {
        defaultProps: { size: "small" },
      },
    },
  },
  frFR,
);

export default theme;
